// ============================================================
// Instance DB backend — ONLY data source (no Supabase, no Firebase).
// DB: Postgres container animeforever-db on 130.210.56.43:5432
// App talks to it via same-origin /api/* (server/ + api/ proxy to PG).
// Table: public.videos (1499 rows).
// ============================================================

// ── In-memory cache ──────────────────────────────────────────
// videosCache: array of normalized rows
let videosCache = null;
let videoIdToKeyMap = {};

const normalizeRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    // camelCase aliases so toAppVideo() works with both shapes
    videoId: row.video_id ?? row.videoId,
    originalUrl: row.original_url ?? row.originalUrl,
    pluginUrl: row.plugin_url ?? row.pluginUrl,
    uploadDate: row.upload_date ?? row.uploadDate,
    durationFormatted: row.duration_formatted ?? row.durationFormatted,
    customTag: row.custom_tag ?? row.customTag,
    lengthSeconds: row.duration ?? row.lengthSeconds,
  };
};

const buildKeyMap = (videosArray) => {
  const map = {};
  if (!Array.isArray(videosArray)) return map;
  for (const v of videosArray) {
    if (!v) continue;
    const vid = v.video_id || v.videoId;
    if (vid) map[String(vid)] = v.id;
    // fallback: parse youtube id from url
    if (!vid && v.url) {
      try {
        const u = new URL(v.url);
        if (u.hostname.includes('youtu.be')) {
          const id = u.pathname.slice(1);
          if (id) map[String(id)] = v.id;
        }
        if (u.searchParams && u.searchParams.has('v')) {
          map[String(u.searchParams.get('v'))] = v.id;
        }
      } catch (e) {
        // ignore malformed URLs when deriving ids
      }
    }
  }
  return map;
};

const apiFetch = async (path, options) => {
  const res = await fetch(path, options);
  if (!res.ok) {
    throw new Error(`instance api ${res.status} for ${path}`);
  }
  return res.json();
};

// ── Load all videos from the Instance DB (paged, max 1000/req) ─
// Returns array (contextApi.js handles both object and array).
export const loadVideosOnce = async () => {
  const PAGE = 1000;
  let all = [];
  for (let offset = 0; ; offset += PAGE) {
    const data = await apiFetch(`/api/videos?limit=${PAGE}&offset=${offset}`);
    const batch = Array.isArray(data) ? data : data.videos || [];
    if (batch.length === 0) break;
    all.push(...batch.map(normalizeRow));
    if (batch.length < PAGE) break;
  }
  if (!all.length) return null;
  videosCache = all;
  videoIdToKeyMap = buildKeyMap(all);
  return all;
};

// ── Subscribe (load once; no realtime channel on instance DB) ──
export const subscribeToVideos = (callback) => {
  loadVideosOnce().then((data) => {
    if (data) callback(data);
  });
  return () => {};
};

const findUuidByVideoId = async (videoId) => {
  const key = String(videoId);
  if (videoIdToKeyMap[key]) return videoIdToKeyMap[key];
  if (!videosCache) {
    await loadVideosOnce();
    if (videoIdToKeyMap[key]) return videoIdToKeyMap[key];
  }
  // direct lookup fallback via instance API
  try {
    const data = await apiFetch(`/api/videos?video_id=${encodeURIComponent(key)}`);
    const rows = Array.isArray(data) ? data : data.videos || [];
    if (rows && rows[0]) {
      videoIdToKeyMap[key] = rows[0].id;
      return rows[0].id;
    }
  } catch (e) {
    // lookup miss — caller treats null as not found
  }
  return null;
};

// ── Views / likes via instance-backed server endpoint ──
const sendIncrement = async (payload) => {
  try {
    const res = await fetch('/api/increment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (!res.ok) {
      console.warn('increment API failed, updating cache only');
    }
  } catch (e) {
    console.warn('increment API error, updating cache only', e);
  }
  // optimistic local update
  if (videosCache) {
    const row = videosCache.find((v) => v.id === payload.id);
    if (row && payload.type in row) {
      row[payload.type] = Math.max(0, (Number(row[payload.type]) || 0) + (Number(payload.delta) || 0));
    }
  }
};

export const incrementView = async (videoId) => {
  const id = await findUuidByVideoId(videoId);
  if (!id) return null;
  await sendIncrement({ id, type: 'views', delta: 1 });
  return id;
};

export const changeLikes = async (videoId, delta) => {
  const id = await findUuidByVideoId(videoId);
  if (!id) return null;
  await sendIncrement({ id, type: 'likes', delta });
  return id;
};

// ── Add video via instance-backed server endpoint ──
export const addVideo = async (videoObj) => {
  const row = {
    video_id: videoObj.videoId ?? videoObj.video_id,
    title: videoObj.title,
    url: videoObj.url,
    original_url: videoObj.originalUrl ?? videoObj.original_url,
    platform: videoObj.platform,
    source: videoObj.source,
    author: videoObj.author ?? 'Uploaded',
    thumbnails: videoObj.thumbnails ?? null,
    thumbnail: videoObj.thumbnail ?? null,
    duration: videoObj.lengthSeconds ?? videoObj.duration ?? 0,
    tags: videoObj.tags ?? null,
    episode: videoObj.episode ?? null,
    upload_date: videoObj.uploadDate ?? videoObj.upload_date ?? null,
    language: videoObj.language ?? 'tamil',
    series: videoObj.series ?? 'Naruto',
    views: 0,
    likes: 0,
  };
  try {
    const created = await apiFetch('/api/videos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(row),
    });
    const rec = created && (created.video || created);
    if (rec && rec.id && videosCache) {
      const n = normalizeRow(rec);
      videosCache.unshift(n);
      videoIdToKeyMap = buildKeyMap(videosCache);
    }
    return rec?.id ?? null;
  } catch (err) {
    console.error('Instance addVideo error:', err);
    return null;
  }
};

const firebaseApi = { loadVideosOnce, subscribeToVideos, incrementView, changeLikes, addVideo };

export default firebaseApi;
